import { FieldType, AssetScope } from 'shared/types/asset';

export interface AssetTypeTemplate {
  name: string;
  description?: string;
  scopes: AssetScope[];
}

export interface FieldDefinitionTemplate {
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  placeholder?: string;
  enumValues?: string[];
  metadata?: {
    unit?: string;
    helpText?: string;
  };
}

export interface AssetCategoryTemplate {
  id: string;
  name: string;
  phosphorIcon: string; // Name of Phosphor icon (e.g., 'Lightning', 'Shower')
  iconColor: string; // Tailwind color class (e.g., 'text-moss-primary')
  description: string;
  displayOrder: number;
  types: AssetTypeTemplate[];
  fields?: FieldDefinitionTemplate[];
}

/**
 * Pre-configured asset category templates for quick setup
 * File requirements are NOT included - users add these custom labels later
 */
export const ASSET_CATEGORY_TEMPLATES: AssetCategoryTemplate[] = [
  {
    id: 'electrical-appliances',
    name: 'Elektrische Toestellen',
    phosphorIcon: 'Lightning',
    iconColor: 'text-moss-primary',
    description: 'Ovens, koelkasten, wasmachines en andere elektrische apparaten',
    displayOrder: 1,
    types: [
      { name: 'Oven', scopes: [AssetScope.UNIT], description: 'Ingebouwde of vrijstaande oven' },
      { name: 'Koelkast', scopes: [AssetScope.UNIT], description: 'Koelkast of koel-vriescombinatie' },
      { name: 'Wasmachine', scopes: [AssetScope.UNIT], description: 'Wasmachine voor kleding' },
      { name: 'Droogkast', scopes: [AssetScope.UNIT], description: 'Droogkast of wasdroger' },
      { name: 'Vaatwasser', scopes: [AssetScope.UNIT], description: 'Afwasmachine' },
      { name: 'Kookplaat', scopes: [AssetScope.UNIT], description: 'Inductie, keramisch of gas' },
      { name: 'Afzuigkap', scopes: [AssetScope.UNIT], description: 'Keukenafzuigkap' },
    ],
    fields: [
      {
        fieldName: 'serial_number',
        displayName: 'Serienummer',
        fieldType: FieldType.STRING,
        isRequired: true,
        placeholder: 'bijv. SN-123456',
      },
      {
        fieldName: 'brand',
        displayName: 'Merk',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'bijv. Bosch, Siemens, AEG',
      },
      {
        fieldName: 'model',
        displayName: 'Model',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'Modelnaam of -nummer',
      },
      {
        fieldName: 'purchase_date',
        displayName: 'Aanschafdatum',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'warranty_expiry',
        displayName: 'Garantie vervalt',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'power',
        displayName: 'Vermogen',
        fieldType: FieldType.NUMBER,
        isRequired: false,
        metadata: {
          unit: 'Watt',
          helpText: 'Elektrisch vermogen in Watt',
        },
      },
      {
        fieldName: 'energy_label',
        displayName: 'Energielabel',
        fieldType: FieldType.ENUM,
        isRequired: false,
        enumValues: ['A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'],
      },
    ],
  },
  {
    id: 'sanitair',
    name: 'Sanitair',
    phosphorIcon: 'Shower',
    iconColor: 'text-blue-600',
    description: 'Toiletten, douches, wastafels en andere sanitaire voorzieningen',
    displayOrder: 2,
    types: [
      { name: 'Toilet', scopes: [AssetScope.ROOM], description: 'WC' },
      { name: 'Douche', scopes: [AssetScope.ROOM], description: 'Douchecabine of inloopdouche' },
      { name: 'Bad', scopes: [AssetScope.ROOM], description: 'Ligbad of hoekbad' },
      { name: 'Wastafel', scopes: [AssetScope.ROOM], description: 'Wastafel in badkamer of toilet' },
      { name: 'Keukenkraan', scopes: [AssetScope.ROOM], description: 'Kraan in keuken' },
      { name: 'Boiler', scopes: [AssetScope.UNIT], description: 'Elektrische boiler voor warm water' },
    ],
    fields: [
      {
        fieldName: 'brand',
        displayName: 'Merk',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'bijv. Grohe, Hansgrohe, Villeroy & Boch',
      },
      {
        fieldName: 'model',
        displayName: 'Model',
        fieldType: FieldType.STRING,
        isRequired: false,
      },
      {
        fieldName: 'installation_date',
        displayName: 'Installatiedatum',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'last_maintenance',
        displayName: 'Laatste onderhoud',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'condition',
        displayName: 'Staat',
        fieldType: FieldType.ENUM,
        isRequired: false,
        enumValues: ['Uitstekend', 'Goed', 'Redelijk', 'Matig', 'Slecht'],
      },
    ],
  },
  {
    id: 'hvac-systems',
    name: 'HVAC Systemen',
    phosphorIcon: 'Thermometer',
    iconColor: 'text-purple-600',
    description: 'Verwarmings-, ventilatie- en airconditioningsystemen',
    displayOrder: 3,
    types: [
      { name: 'Verwarmingsketel', scopes: [AssetScope.BUILDING], description: 'Centrale verwarmingsketel' },
      { name: 'Airconditioning', scopes: [AssetScope.UNIT], description: 'Airco unit' },
      { name: 'Ventilatiesysteem', scopes: [AssetScope.BUILDING], description: 'Mechanische ventilatie' },
      { name: 'Warmtepomp', scopes: [AssetScope.BUILDING], description: 'Warmtepomp voor verwarming' },
      { name: 'Radiatoren', scopes: [AssetScope.ROOM], description: 'Radiatoren per kamer' },
      { name: 'Vloerverwarming', scopes: [AssetScope.ROOM], description: 'Vloerverwarmingssysteem' },
    ],
    fields: [
      {
        fieldName: 'brand',
        displayName: 'Merk',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'bijv. Vaillant, Bosch, Daikin',
      },
      {
        fieldName: 'model',
        displayName: 'Model',
        fieldType: FieldType.STRING,
        isRequired: false,
      },
      {
        fieldName: 'serial_number',
        displayName: 'Serienummer',
        fieldType: FieldType.STRING,
        isRequired: true,
      },
      {
        fieldName: 'installation_year',
        displayName: 'Installatiejaar',
        fieldType: FieldType.NUMBER,
        isRequired: false,
        metadata: {
          helpText: 'Jaar van installatie',
        },
      },
      {
        fieldName: 'capacity',
        displayName: 'Capaciteit',
        fieldType: FieldType.NUMBER,
        isRequired: false,
        metadata: {
          unit: 'kW',
          helpText: 'Verwarmings- of koelcapaciteit',
        },
      },
      {
        fieldName: 'last_service',
        displayName: 'Laatste keuring',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'next_service',
        displayName: 'Volgende keuring',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
    ],
  },
  {
    id: 'elevators-installations',
    name: 'Liften & Installaties',
    phosphorIcon: 'Elevator',
    iconColor: 'text-orange-600',
    description: 'Liften, roltrappen en andere vaste installaties',
    displayOrder: 4,
    types: [
      { name: 'Personenlift', scopes: [AssetScope.BUILDING], description: 'Personenlift' },
      { name: 'Goederenlift', scopes: [AssetScope.BUILDING], description: 'Lift voor goederen' },
      { name: 'Roltrap', scopes: [AssetScope.BUILDING], description: 'Roltrappen' },
      { name: 'Hijskraan', scopes: [AssetScope.BUILDING], description: 'Ingebouwde hijskraan' },
    ],
    fields: [
      {
        fieldName: 'serial_number',
        displayName: 'Serienummer',
        fieldType: FieldType.STRING,
        isRequired: true,
      },
      {
        fieldName: 'brand',
        displayName: 'Merk',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'bijv. Otis, Schindler, KONE',
      },
      {
        fieldName: 'installation_year',
        displayName: 'Installatiejaar',
        fieldType: FieldType.NUMBER,
        isRequired: false,
      },
      {
        fieldName: 'capacity',
        displayName: 'Capaciteit',
        fieldType: FieldType.NUMBER,
        isRequired: false,
        metadata: {
          unit: 'kg',
          helpText: 'Maximale belasting',
        },
      },
      {
        fieldName: 'last_inspection',
        displayName: 'Laatste keuring',
        fieldType: FieldType.DATE,
        isRequired: true,
      },
      {
        fieldName: 'next_inspection',
        displayName: 'Volgende keuring',
        fieldType: FieldType.DATE,
        isRequired: true,
      },
      {
        fieldName: 'inspection_body',
        displayName: 'Keuringsinstantie',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'bijv. KOMO, SGS',
      },
    ],
  },
  {
    id: 'security-systems',
    name: 'Beveiligingssystemen',
    phosphorIcon: 'ShieldCheck',
    iconColor: 'text-teal-600',
    description: 'Alarmsystemen, toegangscontrole en camerabewaking',
    displayOrder: 5,
    types: [
      { name: 'Alarmsysteem', scopes: [AssetScope.BUILDING], description: 'Inbraakalarmsysteem' },
      { name: 'Brandmeldsysteem', scopes: [AssetScope.BUILDING], description: 'Rookmelders en branddetectie' },
      { name: 'Toegangscontrole', scopes: [AssetScope.BUILDING], description: 'Badges, codes, biometrie' },
      { name: 'Camerabewaking', scopes: [AssetScope.BUILDING], description: 'CCTV systeem' },
      { name: 'Intercom', scopes: [AssetScope.BUILDING], description: 'Deurbelsysteem' },
    ],
    fields: [
      {
        fieldName: 'brand',
        displayName: 'Merk',
        fieldType: FieldType.STRING,
        isRequired: false,
      },
      {
        fieldName: 'model',
        displayName: 'Model',
        fieldType: FieldType.STRING,
        isRequired: false,
      },
      {
        fieldName: 'installation_date',
        displayName: 'Installatiedatum',
        fieldType: FieldType.DATE,
        isRequired: false,
      },
      {
        fieldName: 'maintenance_contract',
        displayName: 'Onderhoudscontract',
        fieldType: FieldType.ENUM,
        isRequired: false,
        enumValues: ['Ja', 'Nee'],
      },
      {
        fieldName: 'supplier',
        displayName: 'Leverancier',
        fieldType: FieldType.STRING,
        isRequired: false,
        placeholder: 'Bedrijfsnaam installateur',
      },
    ],
  },
];

/**
 * Get template by ID
 */
export const getTemplateById = (id: string): AssetCategoryTemplate | undefined => {
  return ASSET_CATEGORY_TEMPLATES.find((template) => template.id === id);
};

/**
 * Get all templates sorted by display order
 */
export const getAllTemplates = (): AssetCategoryTemplate[] => {
  return [...ASSET_CATEGORY_TEMPLATES].sort((a, b) => a.displayOrder - b.displayOrder);
};

/**
 * Empty template for starting from scratch
 */
export const EMPTY_TEMPLATE: AssetCategoryTemplate = {
  id: 'empty',
  name: '',
  phosphorIcon: 'Package',
  iconColor: 'text-gray-600',
  description: 'Start met een lege categorie',
  displayOrder: 999,
  types: [],
  fields: [],
};
